import React, { useContext, useState } from 'react';
import {
    Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box, Typography, Chip, Collapse, IconButton, Tooltip
} from '@mui/material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Dashboard, LocalShipping, Replay, LocationOn, Description,
    Store, ExpandLess, ExpandMore,
    BusinessCenterSharp, LocalShippingSharp, MoneyOutlined,
    GroupAdd,
    Scale,
    MapsHomeWorkOutlined,
    DesignServices,
    RequestQuoteOutlined,
    NotesRounded,
    CreditCard,
    RecordVoiceOver,
    Scanner,
    ChevronLeft,
    ChevronRight,
    SettingsInputSvideo,
} from '@mui/icons-material';
import '../../assets/styles/sidebar.scss';
import { CommonContext } from '../../context';
import Logo from '../../logo.png';

const menuItems = [
    {
        section: '', items: [
            { text: 'Dashboard', icon: <Dashboard />, path: '/' },
            {
                text: 'Orders', icon: <LocalShipping />, path: '/orders', isDropdown: true, subItems: [
                    { text: 'Not Shipped', path: '/orders/all-not-shipped' },
                    { text: 'B2C', path: '/orders/b2c' },
                    { text: 'B2B', path: '/orders/b2b' },
                    { text: 'Gaint OFD Orders', path: '/orders/gaint-ofd-orders' },
                    { text: 'Master Data', path: '/master-data' },
                ]
            },
            {
                text: 'Merchants', icon: <BusinessCenterSharp />, path: '/merchants', isDropdown: true, subItems: [
                    { text: 'Registered', path: '/merchants/registered' },
                    { text: 'Signup', path: '/merchants/signup' },
                ]
            },
            { text: 'Courier', icon: <LocalShippingSharp />, path: '/couriers' },
            { text: 'Branch', icon: <LocationOn />, path: '/branches' },
            { text: 'Gaint Warehouses', icon: <Store />, path: '/master-warehouse' },
            {
                text: 'Warehouse', icon: <Store />, path: '/warehouse', isDropdown: true, subItems: [
                    { text: 'List', path: '/warehouse/list' },
                    { text: 'Brands', path: '/warehouse/brands' },
                    { text: 'Category', path: '/warehouse/category' },
                    { text: 'Subcategory', path: '/warehouse/subcategory' },
                    { text: 'Products', path: '/warehouse/products' },
                    { text: 'Supplier', path: '/warehouse/supplier' },
                ]
            },
            {
                text: 'Freight', icon: <MoneyOutlined />, path: '/freight', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/freight/b2c' },
                    { text: 'B2B', path: '/freight/b2b' },
                ]
            },
            {
                text: 'NDR', icon: <Replay />, path: '/ndr', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/ndr/b2c' },
                    { text: 'B2B', path: '/ndr/b2b' },
                ]
            },
            {
                text: 'Weight', icon: <Scale />, path: '/weight', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/weight/b2c' },
                    { text: 'B2B', path: '/weight/b2b' },
                ]
            },
            {
                text: 'Invoices', icon: <Description />, path: '/invoices', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/invoices/b2c' },
                    { text: 'B2B', path: '/invoices/b2b' },
                ]
            },

            { text: 'Credit Notes', icon: <NotesRounded />, path: '/credit-notes' },
            { text: 'Users', icon: <GroupAdd />, path: '/users' },
            { text: 'Serviceability', icon: <DesignServices />, path: '/serviceability' },
            { text: 'Trackings', icon: <MapsHomeWorkOutlined />, path: '/trackings' },
            { text: 'Pickup Requests', icon: <RequestQuoteOutlined />, path: '/pickup-requests' },
            { text: 'Remittance', icon: <CreditCard />, path: '/remittance' },
            { text: 'IVR', icon: <RecordVoiceOver />, path: '/ivr' },
            { text: 'Scan Status', icon: <Scanner />, path: '/scan-status' },
            { text: 'Channel', icon: <SettingsInputSvideo />, path: '/channel' },
        ]
    }
];

const Sidebar = ({ isCollapsed, onToggle, mobileOpen, onMobileClose, isMobile }) => {
    const { user } = useContext(CommonContext);
    const location = useLocation();

    const [openDropdown, setOpenDropdown] = useState({});
    const [isHovered, setIsHovered] = useState(false);
    const isExpanded = isMobile ? true : (isHovered || !isCollapsed);

    const handleToggleDropdown = (key) => {
        if (!isMobile && isCollapsed && !isHovered) {
            onToggle(); // Expand sidebar when clicking dropdown in collapsed state
            return;
        }
        setOpenDropdown((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const HIDE_FOR_MERCHANT = [
        "Merchants",
        "Courier",
        "Branch",
        "IVR",
        "Scan Status",
        "Users",
        "Gaint Warehouses"
    ];

    const sidebarWidth = isCollapsed ? 64 : 180;
    const displayWidth = isExpanded ? (isMobile ? 240 : 180) : 64;

    const drawerContent = (
        <>
            <Box sx={{
                p: isMobile ? 2 : 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 64,
                borderBottom: '1px solid #eee',
                backgroundColor: '#fff',
                position: 'relative',
                zIndex: 1201
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            backgroundColor: '#f5f8f8',
                            padding: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Box
                            component="img"
                            src={Logo}
                            alt="Logo"
                            sx={{ width: '100%', height: '100%', objectFit: "contain" }}
                        />
                    </Box>
                    {isExpanded && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: '#004d40',
                                    lineHeight: 1.2
                                }}
                            >
                                Gaint Logistics
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    color: '#666',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Services
                            </Typography>
                        </Box>
                    )}
                </Box>
                {isMobile && (
                    <IconButton
                        onClick={onMobileClose}
                        size="small"
                        sx={{
                            backgroundColor: '#f5f8f8',
                            '&:hover': { backgroundColor: '#dff5f0' }
                        }}
                    >
                        <ChevronLeft sx={{ fontSize: '1.2rem', color: '#004d40' }} />
                    </IconButton>
                )}
            </Box>
            <Box className="custom-scrollbar" sx={{ overflowY: 'auto', height: 'calc(100vh - 64px)' }}>
                {menuItems.map(({ section, items }, idx) => (
                    <Box key={idx}>
                        {section && (
                            <Typography variant="caption" sx={{ px: 2, pt: 2, pb: 1, color: '#555' }}>
                                {section}
                            </Typography>
                        )}
                        <List disablePadding>
                            {items
                                .filter(({ text }) => {
                                    if (user?.user_type === "Merchant") {
                                        return !HIDE_FOR_MERCHANT.includes(text);
                                    }
                                    if (text === "Channel") return false;
                                    return true;
                                })
                                .map(({ text, icon, path, chip, isDropdown, subItems }) => (
                                    <React.Fragment key={text}>
                                        {isDropdown ? (
                                            <>
                                                <Tooltip title={!isExpanded ? text : ''} placement="right">
                                                    <ListItemButton
                                                        onClick={() => handleToggleDropdown(path)}
                                                        selected={location.pathname.startsWith(path)}
                                                        sx={{
                                                            justifyContent: isExpanded ? 'flex-start' : 'center',
                                                            px: isExpanded ? 2 : 1,
                                                            "&.Mui-selected": {
                                                                backgroundColor: "#dff5f0",
                                                                color: "#004d40",
                                                                borderLeft: "4px solid #2e7d32",
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            minWidth: isExpanded ? 36 : 0,
                                                            justifyContent: 'center'
                                                        }}>
                                                            {icon}
                                                        </ListItemIcon>
                                                        {isExpanded && (
                                                            <>
                                                                <ListItemText primary={text} />
                                                                {openDropdown[path] ? <ExpandLess /> : <ExpandMore />}
                                                            </>
                                                        )}
                                                    </ListItemButton>
                                                </Tooltip>

                                                {isExpanded && (
                                                    <Collapse in={openDropdown[path]} timeout="auto" unmountOnExit>
                                                        <List component="div" disablePadding>
                                                            {subItems
                                                                .filter((sub) => {
                                                                    // Hide "Gaint OFD Orders" and "Master Data" from merchants
                                                                    if (user?.user_type === "Merchant" && (sub.text === "Gaint OFD Orders" || sub.text === "Master Data")) {
                                                                        return false;
                                                                    }
                                                                    return true;
                                                                })
                                                                .map((sub) => (
                                                                    <ListItemButton
                                                                        key={sub.text}
                                                                        component={NavLink}
                                                                        to={sub.path}
                                                                        selected={location.pathname === sub.path}
                                                                        onClick={() => isMobile && onMobileClose()}
                                                                        sx={{
                                                                            pl: 6,
                                                                            "&.Mui-selected": {
                                                                                backgroundColor: "#dff5f0",
                                                                                color: "#004d40",
                                                                                borderLeft: "4px solid #2e7d32",
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ListItemText primary={sub.text} />
                                                                    </ListItemButton>
                                                                ))}
                                                        </List>
                                                    </Collapse>
                                                )}
                                            </>
                                        ) : (
                                            <Tooltip title={!isExpanded ? text : ''} placement="right">
                                                <ListItemButton
                                                    component={NavLink}
                                                    to={path}
                                                    selected={location.pathname === path}
                                                    onClick={() => {
                                                        if (isMobile) {
                                                            onMobileClose();
                                                        } else if (isCollapsed && !isHovered) {
                                                            onToggle();
                                                        }
                                                    }}
                                                    sx={{
                                                        justifyContent: isExpanded ? 'flex-start' : 'center',
                                                        px: isExpanded ? 2 : 1,
                                                        "&.Mui-selected": {
                                                            backgroundColor: "#dff5f0",
                                                            color: "#004d40",
                                                            borderLeft: "4px solid #2e7d32",
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{
                                                        minWidth: isExpanded ? 36 : 0,
                                                        justifyContent: 'center'
                                                    }}>
                                                        {icon}
                                                    </ListItemIcon>
                                                    {isExpanded && (
                                                        <>
                                                            <ListItemText primary={text} />
                                                            {chip && <Chip label={chip} size="small" color="success" />}
                                                        </>
                                                    )}
                                                </ListItemButton>
                                            </Tooltip>
                                        )}
                                    </React.Fragment>
                                ))}
                        </List>
                    </Box>
                ))}
            </Box>
        </>
    );

    return (
        <>
            {/* Desktop Sidebar Elements */}
            {!isMobile && (
                <>
                    <IconButton
                        onClick={onToggle}
                        sx={{
                            position: 'fixed',
                            top: 65,
                            left: isExpanded ? '180px' : '64px',
                            backgroundColor: '#f5f8f8',
                            color: '#004d40',
                            border: '1px solid #ddd',
                            borderLeft: 'none',
                            borderRadius: '0 4px 4px 0',
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            zIndex: 1202,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                                backgroundColor: '#dff5f0',
                                boxShadow: '2px 0 6px rgba(0,0,0,0.15)',
                            },
                            transition: 'left 0.3s ease, background-color 0.2s ease'
                        }}
                        title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    >
                        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                    <Drawer
                        variant="permanent"
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        sx={{
                            flexShrink: 0,
                            width: sidebarWidth,
                            transition: 'width 0.3s ease',
                            zIndex: 1200,
                            [`& .MuiDrawer-paper`]: {
                                width: displayWidth,
                                boxSizing: 'border-box',
                                backgroundColor: '#f5f8f8',
                                borderRight: '1px solid #ddd',
                                transition: 'width 0.3s ease',
                                overflowX: 'hidden',
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                height: '100vh',
                                zIndex: 1200
                            }
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                </>
            )}

            {/* Mobile Sidebar */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onMobileClose}
                    sx={{
                        zIndex: 1300,
                        [`& .MuiDrawer-paper`]: {
                            width: 240,
                            boxSizing: 'border-box',
                            backgroundColor: '#f5f8f8',
                        }
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default Sidebar;
